import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CCNavbar from '../../components/CCNavbar';
import CCFooter from '../../components/CCFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { ccfaqs } from '../../data/faq';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE } from '../../data/legalContent';

function useCCTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'cardcycle');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

function CCFaqItem({ q, a, link, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`cc-faq-item${open ? ' open' : ''}`} data-testid={`cc-faq-item-${idx}`}>
      <div className="cc-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <div className="icon"><i className="fas fa-plus"></i></div>
      </div>
      <div className="cc-faq-a">
        <p>{a}</p>
        {link && (
          <p style={{ marginTop: 8 }}>
            <a href={link.href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cc-primary)', fontWeight: 600 }} data-testid={`cc-faq-link-${idx}`}>
              <i className="fab fa-google-play"></i> {link.label}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

export default function CCFAQ() {
  useCCTheme();
  // On cardcycle.tickleapps.com the app route table owns "/", so the parent
  // Support page must be reached by its full parent-site URL.
  const contactHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/cardcycle/faq'));

  return (
    <PageWrap testId="cc-faq-page">
      <CCNavbar />

      <section className="cc-hero" style={{ minHeight: 'auto', paddingTop: 'calc(var(--nav-h) + 80px)', paddingBottom: 64 }}>
        <div className="cc-hero-orb-1"></div>
        <div className="cc-hero-orb-2"></div>
        <div className="cc-hero-grid-pattern"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div className="eyebrow reveal" style={{ justifyContent: 'center', color: '#5EB7F8' }}>FAQ</div>
          <h1 className="reveal d1" style={{ color: '#fff' }}>Questions about CardCycle.</h1>
          <p className="lead reveal d2" style={{ color: 'rgba(255,255,255,0.7)', margin: '12px auto 0', maxWidth: 540 }}>
            Everything you might want to know about local-first tracking, privacy, and how the app works under the hood.
          </p>
          <p className="reveal d3" style={{ color: 'rgba(255,255,255,0.55)', margin: '10px auto 0', maxWidth: 540, fontSize: '0.85rem' }} data-testid="cc-faq-effective">
            {APP_LEGAL.cardcycle.version} · Effective date: {EFFECTIVE_DATE} · Support: {SUPPORT_EMAIL}
          </p>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--cc-bg)' }}>
        <div className="wrap-sm">
          <div className="reveal" data-testid="cc-faq-list">
            {ccfaqs.map((f, i) => <CCFaqItem key={i} idx={i} {...f} />)}
          </div>

          <div className="reveal" style={{ marginTop: 48, background: 'var(--cc-surf)', border: '1px solid var(--cc-border)', borderRadius: 20, padding: '32px 28px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.1rem' }}>Couldn't find your answer?</h3>
            <p style={{ marginTop: 8, marginBottom: 20 }}>Contact support and tell us which app you use.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-cc" data-testid="cc-faq-email">
                <i className="fas fa-envelope"></i> Email support
              </a>
              {contactHref
                ? <a href={contactHref} className="btn btn-cc-outline" data-testid="cc-faq-contact">Contact form</a>
                : <Link to="/contact" className="btn btn-cc-outline" data-testid="cc-faq-contact">Contact form</Link>}
            </div>
          </div>

          <div className="reveal" style={{ marginTop: 32, textAlign: 'center', fontSize: '0.85rem', color: 'var(--cc-muted)' }}>
            See also: <Link to={brandPath('cardcycle', '/privacy')} style={{ color: 'var(--cc-primary)', fontWeight: 600 }}>Privacy Notice</Link> · <Link to={brandPath('cardcycle', '/terms')} style={{ color: 'var(--cc-primary)', fontWeight: 600 }}>Terms of Use</Link> · <Link to={brandPath('cardcycle', '/account-deletion')} style={{ color: 'var(--cc-primary)', fontWeight: 600 }}>Data Deletion</Link>
          </div>
        </div>
      </section>

      <CCFooter />
    </PageWrap>
  );
}
